<template>
  <div v-if="isFileItem && !multipleItemsSelected">
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="flex items-center opacity-60">
        <div>Timestamp</div>
      </div>
      <div class="text-right">
        <div
          v-for="registration in displayedRegistrations"
          :key="registration.certificateUrl"
          class="last:mb-0 flex items-center justify-end"
        >
          <Icon
            v-if="!registration.origin"
            :size="12"
            name="Dacr"
            class="fill-gray-800 mr-1.5"
          />
          <Avatar v-else class="mr-1.5" :size="12" />

          <div
            class="font-secondary leading-[18px] text-gray-800 flex items-center mr-3"
          >
            {{ formatTimestamp(registration.timestamp) }}
          </div>

          <a
            v-if="registration.certificateUrl"
            :href="registration.certificateUrl"
            target="_blank"
            class="relative group"
          >
            <div class="flex items-center relative z-10 gap-x-1">
              <Icon name="File" :size="12" class="stroke-green-700" />
              <Icon
                name="ChevronUp"
                :size="10"
                class="stroke-green-700 rotate-90"
              />
            </div>
          </a>
        </div>

        <div
          v-if="hasMoreRegistrations"
          class="flex items-center gap-x-1.5 text-[12px] leading-[18px] text-gray-500 font-medium cursor-pointer justify-end mt-1"
        >
          <div class="font-secondary">
            +{{ (registrations?.length || 0) - 3 }} more
          </div>
          <Icon
            name="ChevronUp"
            :size="10"
            class="stroke-gray-500 rotate-180"
          />
        </div>
      </div>
    </div>
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="flex items-center opacity-60">
        {{ pluralize('Owner', owners.length) }}
      </div>
      <div class="max-w-[180px] text-right font-secondary">
        {{ owners.join(', ') }}
      </div>
    </div>
    <div
      class="w-full min-h-[30px] py-1.5 font-primary border-b border-[#E5E5E5] flex items-start justify-between text-[12px] leading-[16px] text-gray-800"
    >
      <div class="flex items-center opacity-60">
        {{ pluralize('Author', authors.length) }}
      </div>
      <div class="max-w-[180px] text-right font-secondary">
        {{ authors.join(', ') || '- -' }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon, Avatar } from '@dacr/ui'
import { formatTimestamp } from '@/shared/utils/datetime'
import type { File } from '@/shared/graphql/router/graphql'
import type { SidebarItem } from './Sidebar.vue'
import { computed } from 'vue'
import pluralize from 'pluralize'

const props = defineProps<{
  item: SidebarItem
  isFileItem: boolean
  multipleItemsSelected: boolean
}>()

const file = computed(() => (props.isFileItem ? (props.item as File) : null))

const registrations = computed(() => file.value?.copyright?.registrations)

const displayedRegistrations = computed(() => registrations.value?.slice(0, 3))

const hasMoreRegistrations = computed(
  () => (registrations.value?.length || 0) > 3
)

const owners = computed(() => {
  if (!file.value) return []
  const ownerNames: string[] = []

  if (file.value.owner?.name) {
    ownerNames.push(file.value.owner.name)
  }

  file.value.coOwners?.edges?.forEach((edge) => {
    if (edge.node?.entity?.name) {
      ownerNames.push(edge.node.entity.name)
    }
  })

  return ownerNames
})

const authors = computed(() => {
  if (!file.value) return []
  const authorNames: string[] = []

  file.value.authors?.edges?.forEach((edge) => {
    if (edge.node?.entity?.name) {
      authorNames.push(edge.node.entity.name)
    }
  })

  return authorNames
})
</script>
